import React, { useState, useEffect } from 'react';
import { Result, message as antdMessage } from 'antd';
import Button from '../common/Button';
import ThreeBackground from './ThreeBackground';
import styles from '@/components/Auth/Auth.module.less';
import { resendVerificationEmail } from '../../libs/api';
import type { components } from '@/api/types';
import { getErrorMessage } from '@/utils/errorHandler';

type RateLimitErrorDto = components['schemas']['RateLimitErrorDto'];

interface RegistrationSuccessProps {
  email: string;
}

const RegistrationSuccess: React.FC<RegistrationSuccessProps> = ({ email }) => {
  const [resending, setResending] = useState(false);
  const [retryAfter, setRetryAfter] = useState<number>(0);

  const handleResend = async () => {
    setResending(true);
    setRetryAfter(0);
    try {
      const result = await resendVerificationEmail(email);
      antdMessage.success(result.message || '验证邮件已重新发送，请查收邮箱。');
    } catch (error: unknown) {
      const errorData = (error as { details?: unknown })?.details as RateLimitErrorDto | undefined;
      if (errorData && typeof errorData === 'object' && 'retryAfter' in errorData) {
        setRetryAfter(errorData.retryAfter);
        antdMessage.warning(`发送频率过高，请等待 ${errorData.retryAfter} 秒后重试`);
      } else {
        antdMessage.error(getErrorMessage(error, '发送失败，请稍后重试。'));
      }
    } finally {
      setResending(false);
    }
  };

  useEffect(() => {
    if (retryAfter > 0) {
      const timer = setInterval(() => {
        setRetryAfter(prev => {
          if (prev <= 1) {
            clearInterval(timer);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
      return () => clearInterval(timer);
    }
  }, [retryAfter]);

  return (
    <div className={styles['auth-page']}>
      <ThreeBackground />
      <div className={styles['auth-container']}>
        <div className={styles['auth-form']}>
          <Result
            status="success"
            title="✉️ 验证邮件已发送"
            subTitle={
              <div style={{ fontSize: 14, lineHeight: 1.8, textAlign: 'center' }}>
                <p style={{ fontSize: 15, marginBottom: 12 }}>
                  我们已向 <strong style={{ color: '#6a0dad' }}>{email}</strong> 发送了验证邮件
                </p>
                <p style={{ marginBottom: 12 }}>请查收邮箱并点击验证链接以激活您的账户</p>
                <p style={{ color: '#999', fontSize: 13, marginTop: 16 }}>
                  💡 如果您没有收到邮件，请检查垃圾邮件文件夹
                </p>
                {retryAfter > 0 && (
                  <p style={{ marginTop: 12, color: '#ff9800', fontSize: 13 }}>
                    ⏱️ 请等待 {retryAfter} 秒后重新发送
                  </p>
                )}
              </div>
            }
            extra={[
              <Button
                key="resend"
                onClick={handleResend}
                disabled={resending || retryAfter > 0}
                className="primaryButton"
              >
                {resending
                  ? '发送中...'
                  : retryAfter > 0
                    ? `等待 ${retryAfter} 秒`
                    : '重新发送验证邮件'}
              </Button>,
            ]}
          />
        </div>
      </div>
    </div>
  );
};

export default RegistrationSuccess;
